import React from 'react'
import moment from 'moment'
import Link from 'redux-first-router-link'
import NoticeBar from 'components/NoticeBar'
import LegacyLink from 'components/LegacyLink'
import { timeInHuman } from 'util/date'

import {
  SETTINGS_BILLING_PAGE,
  SETTINGS_INTEGRATIONS_PROVIDER_PAGE,
} from 'subapps/settings/types'
import styles from './styles.less'

export default function AppNoticesView({
  showDemoMailboxBar,
  showExpiredBilling,
  showGitHubReauth,
  showPendingCancellation,
  cancelsAt,
  showCreditCardExpireSoon,
  showCreditCardExpired,
  creditCard,
  showLastBillingFailed,
  showTrialMessage,
  trialDaysLeft,
  showChatLaunch,
  showIOSMobileAppLaunch,
}) {
  const notices = []
  // The order of the notice below reflects their importance top -> most important
  if (showLastBillingFailed) {
    notices.push(
      <NoticeBar key="app-notice-billingfailed">
        We are unable to charge the credit card associated with your account.
        <Link
          className={styles.buttonLink}
          to={{ type: SETTINGS_BILLING_PAGE }}
        >
          Update credit card
        </Link>
      </NoticeBar>
    )
  }
  if (showExpiredBilling) {
    notices.push(
      <NoticeBar key="app-notice-expiredbilling" noticeFlagText="HEADS UP!">
        To continue using Groove please update your billing information.
      </NoticeBar>
    )
  }
  if (showPendingCancellation) {
    notices.push(
      <NoticeBar
        key="app-notice-pendingcancellation"
        flagName="app-notice-pendingcancellation"
        dismissible
        forceShow
      >
        Your account is scheduled to close on{' '}
        {timeInHuman(cancelsAt, { yearFormat: '%y' })}
      </NoticeBar>
    )
  }
  if (showCreditCardExpired) {
    notices.push(
      <NoticeBar key="app-notice-cardexpired">
        The credit card associated to your account has expired.
        <Link
          className={styles.buttonLink}
          to={{ type: SETTINGS_BILLING_PAGE }}
        >
          Update credit card
        </Link>
      </NoticeBar>
    )
  }
  if (showCreditCardExpireSoon) {
    const { expiryMonth, expiryYear } = creditCard
    const expireDate = moment(`${expiryYear}-${expiryMonth}-01`, 'YYYY-MM-DD')
    const flagName = `has_seen_credit_card_expire_soon_${expiryYear}_${expiryMonth}`
    notices.push(
      <NoticeBar
        key="app-notice-cardexpirysoon"
        flagName={flagName}
        forceShow
        dismissible
      >
        The credit card associated to your account will be expiring at the end
        of {expireDate.format('MMM YYYY')}.
        <Link
          className={styles.buttonLink}
          to={{ type: SETTINGS_BILLING_PAGE }}
        >
          Update credit card
        </Link>
      </NoticeBar>
    )
  }
  if (showTrialMessage) {
    notices.push(
      <NoticeBar
        key="app-notice-trialinfo"
        flagName="app-notice-trialinfo"
        into
        dismissible
        forceShow
      >
        {trialDaysLeft <= 1 && 'You have less than a day left in your trial'}
        {trialDaysLeft > 1 &&
          trialDaysLeft < 2 &&
          'You have 1 day left in your trial'}
        {trialDaysLeft >= 2 &&
          `You have ${trialDaysLeft.toFixed(0)} days left in your trial`}
        <Link
          className={styles.buttonLink}
          to={{ type: SETTINGS_BILLING_PAGE }}
        >
          Subscribe now
        </Link>
      </NoticeBar>
    )
  }
  if (showDemoMailboxBar) {
    notices.push(
      <NoticeBar key="app-notice-demomailbox" noticeFlagText="HEADS UP!">
        You’re currently in demo mode. To start using Groove for real you’ll
        want to add your first mailbox.
        <LegacyLink
          as="a"
          async={false}
          skipModal
          target="_new"
          path="/groove_client/settings/mailboxes"
          className={styles.buttonLink}
        >
          Add your mailbox
        </LegacyLink>
      </NoticeBar>
    )
  }

  if (showGitHubReauth) {
    notices.push(
      <NoticeBar key="app-notice-githubReauth">
        The GitHub integration has been updated and needs to be re-installed.
        Read more about it in our{' '}
        <Link
          className={styles.demoBarLinkInline}
          target="_blank"
          to="https://help.groovehq.com/help/github-integration#existing-users"
        >
          KB article
        </Link>{' '}
        or go to the{' '}
        <Link
          className={styles.demoBarLinkInline}
          to={{
            type: SETTINGS_INTEGRATIONS_PROVIDER_PAGE,
            payload: { provider: 'github' },
          }}
        >
          integration settings
        </Link>{' '}
        to continue.
      </NoticeBar>
    )
  }

  if (showChatLaunch) {
    notices.push(
      <NoticeBar
        key="app-notice-chatlaunch"
        flagName="app-notice-chatlaunch"
        noticeFlagText="NEW"
        dismissible
        forceShow
      >
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.groovehq.com/blog/product-update-live-chat"
          className={styles.demoBarLinkInline}
        >
          Live chat is here
        </a>
        <span>&nbsp;{'\u{1F389}'}&nbsp;</span>
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.groovehq.com/blog/product-update-live-chat"
          className={styles.demoBarLinkInline}
        >
          Read all about it here
        </a>
      </NoticeBar>
    )
  }

  if (showIOSMobileAppLaunch) {
    notices.push(
      <NoticeBar
        key="seen-app-notice-iosapp-launch"
        flagName="seen-app-notice-iosapp-launch"
        dismissible
        forceShow
      >
        We launched our new iOS app!<span>&nbsp;{'\u{1F389}'}&nbsp;</span>Read
        our{' '}
        <a
          className={styles.demoBarLinkInline}
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.groovehq.com/blog/product-update-ios-app"
        >
          blog article
        </a>{' '}
        to learn more or visit the{' '}
        <a
          className={styles.demoBarLinkInline}
          target="_blank"
          rel="noopener noreferrer"
          href="https://apps.apple.com/us/app/groove-help-desk/id1509476768"
        >
          App Store
        </a>{' '}
        page.
      </NoticeBar>
    )
  }

  if (notices.length === 0) return null
  return notices.length > 0 ? notices[0] : null
}
